import { Injectable, inject } from '@angular/core';
import {
    FormArray,
    FormGroup,
    UntypedFormArray,
    UntypedFormControl,
    Validators,
} from '@angular/forms';
import {
    FieldDefinition,
    FormConditions,
    FormElementLayoutDefinition,
    FormElementType,
    FormFieldType,
    FormFunctionResult,
} from '@wdx/shared/utils';
import { Observable } from 'rxjs';
import { DynamicFormsService } from '../../+state/dynamic-forms';
import { CURRENCY_FIELD_TYPES } from '../../constants';
import { LayoutAndFieldDefinition } from '../../models';
import { BespokeValidators } from '../../validators';
import { FormArrayService } from '../form-array/form-array.service';
import { FormControlService } from '../form-control/form-control.service';
import { FormValidationService } from '../form-validation';

@Injectable()
export class FormProcessConditionDataService {
    private dynamicFormsService = inject(DynamicFormsService);
    private formValidationService = inject(FormValidationService);
    private formControlService = inject(FormControlService);
    private formArrayService?: FormArrayService;

    setFormArrayService(formArrayService: FormArrayService) {
        this.formArrayService = formArrayService;
    }

    /**
     *
     * @param form - This is the angular FormGroup
     * @param names - This should be a array of array names in hierarchical order
     * @param index - This is for when dealing with arrays with multiple records to get a index
     * @returns FormArray
     */
    getFormArray(
        form: FormGroup,
        names: string[],
        index?: number,
    ): UntypedFormArray | undefined {
        /**
         * name[0] - This gets the first string in the array of name
         */
        const FIRST_ARRAY = form?.get(names[0]) as UntypedFormArray;

        if (!names?.length && !form && !FIRST_ARRAY) {
            return;
        }

        if (names?.length === 1) {
            return FIRST_ARRAY;
        }

        if (names?.length > 1) {
            return FIRST_ARRAY?.at(index as number)?.get(
                names[1],
            ) as UntypedFormArray;
        }
        return;
    }

    sum(form: FormGroup, of: string[], indexes?: number[]): number {
        let totalSum = 0;

        of.map((formField) => {
            const FORM_CONTROLS = this.formControlService.getFormControlData(
                form,
                formField,
                indexes,
            );
            for (const FORM_CONTROL_DATA of FORM_CONTROLS) {
                const FIELD_VAL = FORM_CONTROL_DATA.formControl
                    ? FORM_CONTROL_DATA.formControl.value
                    : null;
                const isFieldCurrencyType = FIELD_VAL?.amount;
                const AMOUNT = isFieldCurrencyType
                    ? FIELD_VAL.amount
                    : FIELD_VAL;

                totalSum =
                    typeof AMOUNT === 'number' ? totalSum + AMOUNT : totalSum;
            }
        });

        return totalSum;
    }

    recalculate(
        form: FormGroup,
        field: string,
        conditions: FormConditions | undefined,
        indexes?: number[],
    ): number {
        const CALCULATIONS = conditions?.calculations;
        const MAX = (
            conditions ? conditions?.calculations?.length : 0
        ) as number;
        let totalSum;
        let count = 0;

        while (count < MAX) {
            if (CALCULATIONS?.[count]?.field === field) {
                if (CALCULATIONS?.[count]?.equalTo?.sum) {
                    totalSum = this.sum(
                        form,
                        CALCULATIONS?.[count]?.equalTo?.sum?.of as string[],
                        indexes,
                    );
                }

                count = MAX;
            }

            count++;
        }

        return totalSum as number;
    }

    updateValueAccordingToType(
        fieldType: FormFieldType,
        currentValue: any,
        newValue: any,
    ) {
        if (CURRENCY_FIELD_TYPES.includes(fieldType)) {
            return {
                isoCode: currentValue?.isoCode ? currentValue?.isoCode : '',
                amount: newValue,
            };
        }

        return newValue;
    }

    /**
     *
     * @param {{formId: string; formValue: any;}} form - This is the formId and formValue. Please note that the formValue needs to be used
     * @param functionName - This is the name of the function that needs to be called
     * @returns Observable<FormFunctionResult> - This should return an observable with FormFunctionResult
     */
    getApiFunctionTrigger(
        form: {
            formId: string;
            formValue: any;
        },
        functionName: string,
        subFormName?: string,
        subFormIndex?: number,
        isSubform?: boolean,
    ): Observable<FormFunctionResult> {
        return this.dynamicFormsService.getFormFunctionResult(
            form.formId,
            {
                functionName,
                formData: form.formValue,
            },
            subFormName,
            subFormIndex,
            isSubform,
        );
    }

    findFormControlUpdateValue(
        formControl: UntypedFormControl,
        newValue: any | null | undefined,
        formFieldType: FormFieldType,
        pristine: boolean,
    ): boolean {
        if (formControl) {
            const val = this.isNull(newValue)
                ? null
                : this.updateValueAccordingToType(
                      formFieldType,
                      formControl.value,
                      newValue,
                  );
            if (val === formControl.value) {
                return false;
            }
            this.updateFormControlValue(
                formControl,
                this.isNull(newValue)
                    ? null
                    : this.updateValueAccordingToType(
                          formFieldType,
                          formControl.value,
                          newValue,
                      ),
                pristine,
            );
            return true;
        }
        return false;
    }

    updateFormControlValue(
        formControl: UntypedFormControl,
        val: any,
        pristine: boolean,
        preventEmitter?: boolean,
    ): void {
        const touched = formControl?.touched;
        const controlIsArray = formControl instanceof FormArray;
        const { path } =
            this.formControlService.getMetadataFromFormControl(formControl);
        if (controlIsArray && Array.isArray(val)) {
            this.formArrayService?.updateArrayToLength(
                path as string,
                val.length,
                formControl,
            );
        }

        if (formControl) {
            if (!pristine && !preventEmitter) {
                formControl.patchValue(val);
            }

            if (pristine || preventEmitter) {
                formControl.patchValue(val, {
                    onlySelf: false,
                    emitEvent: false,
                });
            }

            if (pristine) {
                formControl.markAsPristine();
            }

            if (!touched || pristine) {
                formControl.markAsUntouched();
            }
        }
    }

    updateFormControlRequiredStatues(
        formControl: UntypedFormControl | UntypedFormArray,
        isRequired?: boolean | null,
        isEditable?: boolean,
    ): void {
        if (isEditable && isRequired && typeof isRequired === 'boolean') {
            formControl?.addValidators(Validators.required);
        } else if (!isEditable || !isRequired) {
            formControl?.removeValidators(Validators.required);
        }
        formControl?.updateValueAndValidity();
    }

    updateSubFormControlMinValidator(
        formControl: UntypedFormControl | UntypedFormArray,
        fieldDefinition: FormElementLayoutDefinition & FieldDefinition,
        subFormDefinition?: FieldDefinition,
    ): void {
        let min = fieldDefinition?.min;
        if (typeof subFormDefinition?.min === 'number') {
            min =
                typeof min === 'number'
                    ? Math.max(min, subFormDefinition.min)
                    : subFormDefinition.min;
        }
        if (typeof min === 'number') {
            const VALIDATOR = this.formValidationService.getMinValidator(
                fieldDefinition.fieldType ||
                    (fieldDefinition?.elementType as FormElementType),
                min,
            );

            if (VALIDATOR.length) {
                formControl.addValidators(VALIDATOR[0]);
                formControl.updateValueAndValidity();
            }
        }
    }

    updateSubFormControlMaxValidator(
        formControl: UntypedFormControl | UntypedFormArray,
        fieldDefinition: FormElementLayoutDefinition & FieldDefinition,
        subFormDefinition?: FieldDefinition,
    ): void {
        let max = fieldDefinition?.max;
        if (typeof subFormDefinition?.max === 'number') {
            max =
                typeof max === 'number'
                    ? Math.min(max, subFormDefinition.max)
                    : subFormDefinition.max;
        }
        if (typeof max === 'number') {
            const VALIDATOR = this.formValidationService.getMaxValidator(
                fieldDefinition.fieldType ||
                    (fieldDefinition?.elementType as FormElementType),
                max,
            );

            if (VALIDATOR.length) {
                formControl.addValidators(VALIDATOR[0]);
                formControl.updateValueAndValidity();
            }
        }
    }

    updateFormControlValidation(
        formControl: UntypedFormControl,
        fieldDefinition: FieldDefinition,
        layout: FormElementLayoutDefinition,
    ): void {
        const VALIDATORS = this.formValidationService.getValidators(
            fieldDefinition,
            layout,
        );

        if (formControl) {
            formControl.clearValidators();
            formControl.updateValueAndValidity();

            formControl.setValidators(VALIDATORS);
            formControl.updateValueAndValidity();
        }
    }

    /**
     * updateValidation will call the following to method of the same class
     * updateFormControlRequiredStatues
     *
     * @param formControl - This needs to be FormControl
     * @param fieldDefinition - This needs to be an object that has isDisabled || isRequired
     * @param subFormDefinition - Optionally accepts a second definition for subform array controls
     */
    updateValidation(
        formControl: UntypedFormControl | UntypedFormArray,
        elementLayoutDefinition: LayoutAndFieldDefinition,
        subFormDefinition?: FieldDefinition,
    ): void {
        if (elementLayoutDefinition.isReadOnly && !elementLayoutDefinition?.validateWhenDisabled) {
            return;
        }
        const hasMin =
            subFormDefinition &&
            (!!elementLayoutDefinition?.min || !!subFormDefinition?.min);

        this.updateFormControlRequiredStatues(
            formControl,
            hasMin ?? elementLayoutDefinition?.isRequired,
            !((
                (elementLayoutDefinition?.isDisabled || elementLayoutDefinition?.isReadOnly) &&
                !elementLayoutDefinition?.validateWhenDisabled
            )),
        );

        this.updateSubFormControlMinValidator(
            formControl,
            elementLayoutDefinition,
            subFormDefinition,
        );

        this.updateSubFormControlMaxValidator(
            formControl,
            elementLayoutDefinition,
            subFormDefinition,
        );

        if (elementLayoutDefinition.mask) {
            const validator = BespokeValidators.regex(
                elementLayoutDefinition.mask,
                elementLayoutDefinition.maskDescription,
            );
            formControl.addValidators(validator);
            formControl.updateValueAndValidity();
        }
    }

    /**
     * Returns true if string is representation of YAML null value
     * @param value
     * @returns true if null string value
     */
    isNull(value: string): boolean {
        return value === 'null' || value === '~';
    }
}
